/**
 * ConfirmationModal component for confirming destructive actions
 * Feature: User management - block/delete confirmation
 */
import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

export type ConfirmationVariant = 'danger' | 'warning';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
}: ConfirmationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: 'bg-brand-error/10 text-brand-error',
      button: 'bg-brand-error hover:opacity-90 focus:ring-brand-error',
    },
    warning: {
      icon: 'bg-brand-warning/10 text-brand-warning',
      button: 'bg-brand-warning hover:opacity-90 focus:ring-brand-warning',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-card bg-brand-bg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-start gap-4 px-6 py-4">
            <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-pill ${styles.icon}`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-brand-text">
                {title}
              </h3>
              <p className="mt-2 text-sm text-brand-muted">
                {message}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-brand-muted hover:text-brand-text disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-brand-border px-6 py-4 bg-brand-surface">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-brand-text bg-brand-bg border border-brand-border rounded-input hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium text-brand-surface border border-transparent rounded-input focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${styles.button}`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-pill h-4 w-4 border-b-2 border-brand-surface"></div>
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
